// @flow
import React, {useState, useCallback, useEffect} from 'react';
import {colors} from '../../../../../assets/styles/colors';
import {
  Divider,
  Grid,
  Hidden,
  makeStyles,
  MenuItem,
  Typography,
  IconButton,
} from '@material-ui/core';

import addProductFeatureIcon from '../../../../../assets/img/add-product-feature.svg';

import CustomButton from '../../../../../elements/CustomButton/CustomButton';
import CustomTextField from '../../../../../elements/CustomTextField/CustomTextField';
import CustomSelect from '../../../../../elements/CustomSelect/CustomSelect';
import {PRODUCT_FEATURES} from '../../../utils';
import TextEditor from './TextEditor/TextEditor';
import {useIsFeatureInTier} from '../../../../../utils/component';
import PremiumFeatureBadge from '../../../../../elements/PremiumFeatureBadge/PremiumFeatureBadge';

import {useFormContext} from 'react-hook-form';

import type {Control} from 'react-hook-form';
import type {Theme} from '@material-ui/core';
import {DeleteOutline, Visibility, Edit} from '@material-ui/icons';
import {useDebounceEffect} from '../../../../../utils/react';

type Props = {
  errors: {[key: string]: any},
  productDescription: string,
  setProductDescription: (text: string) => void,
  control: Control,
  setEnabledProductFeatures: (value: any) => void,
  disabled: boolean,
  initialProductFeatures?: Array<{
    id: string,
    name: string,
    description: string,
  }>,
};

const useStyles = makeStyles((theme: Theme) => ({
  contentSection: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: theme.spacing(2),
    borderRadius: '12px',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    padding: theme.spacing(3),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2),
    },
    background: 'white',
  },
  contentSectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  sectionTitle: {
    fontSize: '1rem',
    display: 'flex',
    alignItems: 'center',
    fontWeight: 600,
  },
  sectionDescription: {
    color: colors.grey700,
    fontSize: '0.875rem',
  },
  sectionSubtitle: {
    color: colors.grey700,
    fontSize: 14,
    fontWeight: 400,
    '& span': {
      color: colors.grey900,
      fontWeight: 500,
    },
  },
  divider: {
    margin: theme.spacing(2, 0),
  },
  sectionCheckbox: {
    '& .MuiFormControlLabel-label': {
      color: colors.grey700,
      fontSize: '0.875rem',
    },
  },
  spaceBetween: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  selectProducts: {
    width: '100%',
    borderRadius: 8,
    '& span': {
      borderRadius: 8,
    },
  },
  wrapSelect: {
    width: '100%',
    margin: theme.spacing(1, 0),
  },
  select: {
    height: 28,
  },
  selectItem: {
    boxSizing: 'border-box',
    margin: theme.spacing(0, 1),
    borderRadius: '6px',
    maxWidth: '100%',
  },
  wrapIcon: {
    display: 'flex',
    alignItems: 'center',
  },
  listProductFeatures: {
    margin: theme.spacing(2, 0),
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  wrapProductFeature: {
    padding: theme.spacing(1.5),
    borderRadius: 12,
    background: colors.grey100,
    boxShadow:
      'inset 0px -1px 4px rgba(0, 0, 0, 0.04), inset 0px 1px 4px rgba(0, 0, 0, 0.08)',
  },
  addFeatureButton: {
    borderRadius: 8,
    width: '100%',
    border: '1px dashed #B8B8C7',
    paddingTop: theme.spacing(2.25),
    paddingBottom: theme.spacing(2.25),
    '& img': {
      marginRight: theme.spacing(1),
    },
  },
  wrapSubtitleSection: {
    [theme.breakpoints.down('md')]: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  icon: {
    marginLeft: '1rem',
  },
  textEditor: {
    marginBottom: '2rem',
  },
  deleteFeatureBtn: {
    position: 'absolute',
    top: 0,
    right: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
  },
}));

const MAX_PRODUCT_FEATURES = 6;

export default function ProductFeaturesSection(props: Props) {
  const {
    productDescription,
    setProductDescription,
    disabled,
    initialProductFeatures,
  } = props;

  const {setValue} = useFormContext();

  const classes = useStyles();

  const [productFeatures, setProductFeatures] = useState([]);

  const hasProductFeaturesFeature = useIsFeatureInTier(
    'PRODUCT_SPECIFICATIONS'
  );
  const [showIcons] = useState(true);

  const [mdEditorPreview, setMdEditorPreview] = useState(false);

  const handleDescription = useCallback(
    (value: string) => {
      setProductDescription(value);
    },
    [setProductDescription]
  );

  useDebounceEffect(
    () => {
      setValue('productFeatures', productFeatures);
    },
    750,
    [productFeatures]
  );

  useEffect(() => {
    setProductFeatures(initialProductFeatures ?? []);
  }, [initialProductFeatures]);

  const addProductFeature = (name: string, description: string) => {
    if (productFeatures.length >= MAX_PRODUCT_FEATURES) {
      return;
    }

    const newFeature = {
      id:
        Date.now().toString() +
        Math.random()
          .toString(36)
          .substring(2, 15),
      name: '',
      description: '',
    };

    setProductFeatures(prevFeatures => [...prevFeatures, newFeature]);
  };

  const removeProductFeature = (id: string) => {
    setProductFeatures(prevFeatures =>
      prevFeatures.filter(feature => feature.id !== id)
    );
  };

  const updateProductFeature = (
    id: string,
    {
      name,
      description,
    }: {
      name?: string,
      description?: string,
    }
  ) => {
    setProductFeatures(prevFeatures =>
      prevFeatures.map(feature => {
        if (feature.id === id) {
          const newFeature = {
            ...feature,
            name: name || feature.name,
            description: description || feature.description,
          };
          return newFeature;
        }
        return feature;
      })
    );
  };

  const buildSelectOptions = () => {
    return PRODUCT_FEATURES.map(({name, icon}, index) => (
      <MenuItem
        name={name}
        className={classes.selectItem}
        key={name}
        value={name}
      >
        <div className={classes.wrapIcon}>
          {name}
          {showIcons && icon ? (
            <img src={icon} className={classes.icon} alt={'icon ' + name} />
          ) : null}
        </div>
      </MenuItem>
    ));
  };

  return (
    <section className={classes.contentSection}>
      <header className={classes.contentSectionHeader}>
        <Typography className={classes.sectionTitle}>Descripción :</Typography>

        <CustomButton
          variant="contained"
          color="primary"
          className={classes.button}
          startIcon={
            mdEditorPreview ? (
              <Edit fontSize="small" />
            ) : (
              <Visibility fontSize="small" />
            )
          }
          onClick={() => {
            setMdEditorPreview(ps => !ps);
          }}
        >
          {mdEditorPreview ? 'Editar' : 'Vista previa'}
        </CustomButton>
      </header>
      <div className={classes.textEditor}>
        <TextEditor
          placeholder="Descripción ..."
          text={productDescription}
          handleDescription={handleDescription}
          previewMode={mdEditorPreview ? 'preview' : 'edit'}
        />
      </div>
      <div className={classes.spaceBetween}>
        <Typography className={classes.sectionTitle}>
          Características principales{' '}
          {!hasProductFeaturesFeature && <PremiumFeatureBadge />}
        </Typography>
        {/* <div>
          {enabledProductFeatures ? 'Activado' : 'Desactivado'}
          <IosSwitch
            checked={enabledProductFeatures}
            onChange={handleEnableProductFeatures}
            disabled={!hasProductFeaturesFeature}
          />
        </div> */}
      </div>{' '}
      <Typography className={classes.sectionDescription}>
        Agrega un máximo de 6 características que quieres resaltar de tu
        producto como el material, marca modelo, etc.
      </Typography>
      <Divider className={classes.divider} />
      <div className={`${classes.spaceBetween} ${classes.wrapSubtitleSection}`}>
        <Typography className={classes.sectionSubtitle}>
          <span>Característica y Respuesta</span>
          <Hidden smDown> (máximo 28 caracteres - incluyendo espacios)</Hidden>
          <Hidden mdUp>
            <br />
            Máximo 28 caracteres - incluye espacios
          </Hidden>
        </Typography>
        {/*      <FormControlLabel
          control={
            <Checkbox
              checked={showIcons}
              onChange={handleShowIcons}
              name="checkedI"
              color="primary"
            />
          }
          className={classes.sectionCheckbox}
          label="Mostrar iconos"
        /> */}
      </div>
      <Grid container spacing={2}>
        {productFeatures.map((productFeature, idx) => (
          <Grid item xs={12} key={productFeature.id}>
            <div className={classes.wrapProductFeature}>
              <Grid container spacing={1}>
                <Grid item xs={12} md={6}>
                  <CustomSelect
                    classes={{root: classes.select}}
                    fullWidth
                    value={productFeature.name || ''}
                    disabled={disabled}
                    onChange={e => {
                      updateProductFeature(productFeature.id, {
                        name: e.target.value,
                      });
                    }}
                  >
                    <option defaultValue="" hidden />
                    {buildSelectOptions()}
                  </CustomSelect>
                </Grid>
                <Grid item xs={12} md={6}>
                  <CustomTextField
                    label="Escribe aquí la descripción"
                    value={productFeature.description || ''}
                    type="text"
                    disabled={disabled}
                    onChange={e => {
                      updateProductFeature(productFeature.id, {
                        description: e.target.value,
                      });
                    }}
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          edge="end"
                          onClick={() => {
                            removeProductFeature(productFeature.id);
                          }}
                        >
                          <DeleteOutline color="error" />
                        </IconButton>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
            </div>
          </Grid>
        ))}
        <Grid item xs={12}>
          <CustomButton
            className={classes.addFeatureButton}
            onClick={() => {
              addProductFeature('', '');
            }}
            disabled={
              disabled || productFeatures.length >= MAX_PRODUCT_FEATURES
            }
          >
            <img
              style={{
                opacity:
                  productFeatures.length >= MAX_PRODUCT_FEATURES ? 0.5 : 1,
              }}
              src={addProductFeatureIcon}
              alt="add product feature"
            />
            Agregar otra característica
          </CustomButton>
        </Grid>
      </Grid>
    </section>
  );
}
